/**
 * API Client
 *
 * Base client for mock API operations.
 * Provides consistent response structure and error handling.
 */

import type { ApiResponse, PaginatedResponse, PaginationParams } from '../types'
import { delay } from './mockDb'
export { delay }

/**
 * Default delay for simulating network latency (ms)
 */
const DEFAULT_DELAY = 100

/**
 * Configuration for API requests
 */
export interface ApiConfig {
  delay?: number
}

/**
 * Create a success response
 */
export function success<T>(data: T): ApiResponse<T> {
  return {
    success: true,
    data,
  }
}

/**
 * Create an error response
 */
export function error<T>(message: string): ApiResponse<T> {
  return {
    success: false,
    data: null as unknown as T,
    error: message,
  }
}

/**
 * Create a paginated response
 */
export function paginated<T>(
  items: T[],
  params: PaginationParams = {}
): PaginatedResponse<T> {
  const page = params.page ?? 1
  const limit = params.limit ?? 20
  const total = items.length
  const totalPages = Math.ceil(total / limit)

  const start = (page - 1) * limit
  const end = start + limit
  const data = items.slice(start, end)

  return {
    success: true,
    data,
    pagination: {
      page,
      limit,
      total,
      totalPages,
      hasMore: page < totalPages,
    },
  }
}

/**
 * Wrap an async operation with delay and error handling
 */
export async function withDelay<T>(
  operation: () => T | Promise<T>,
  config: ApiConfig = {}
): Promise<T> {
  await delay(config.delay ?? DEFAULT_DELAY)
  return operation()
}

/**
 * Wrap an operation in a try-catch and return ApiResponse
 */
export async function safeExecute<T>(
  operation: () => T | Promise<T>,
  config: ApiConfig = {}
): Promise<ApiResponse<T>> {
  try {
    await delay(config.delay ?? DEFAULT_DELAY)
    const result = await operation()
    return success(result)
  } catch (e) {
    const message = e instanceof Error ? e.message : 'An unexpected error occurred'
    return error(message)
  }
}

/**
 * Simple password hashing (NOT secure - for mock purposes only)
 * In production, this would be done server-side with bcrypt
 */
export function mockHash(password: string): string {
  // Simple hash for development - NOT SECURE
  let hash = 0
  for (let i = 0; i < password.length; i++) {
    const char = password.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  return `mock_hash_${Math.abs(hash).toString(16)}`
}

/**
 * Verify a password against a hash
 */
export function mockVerify(password: string, hash: string): boolean {
  return mockHash(password) === hash
}

/**
 * Generate a mock JWT token
 */
export function generateMockToken(userId: string, expiresInHours: number = 24): string {
  const payload = {
    sub: userId,
    iat: Date.now(),
    exp: Date.now() + expiresInHours * 60 * 60 * 1000,
  }
  // Base64 encode (NOT a real JWT - for mock purposes only)
  return `mock_token_${btoa(JSON.stringify(payload))}`
}

/**
 * Decode a mock token
 */
export function decodeMockToken(token: string): { sub: string; exp: number } | null {
  try {
    const base64 = token.replace('mock_token_', '')
    const payload = JSON.parse(atob(base64))
    return payload
  } catch {
    return null
  }
}

/**
 * Check if a token is expired
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeMockToken(token)
  if (!payload) return true
  return payload.exp < Date.now()
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate password strength
 * Requirements: 8+ chars, 1 uppercase, 1 lowercase, 1 number
 */
export function isValidPassword(password: string): { valid: boolean; message?: string } {
  if (password.length < 8) {
    return { valid: false, message: 'Password must be at least 8 characters' }
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one uppercase letter' }
  }
  if (!/[a-z]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one lowercase letter' }
  }
  if (!/[0-9]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one number' }
  }
  return { valid: true }
}

/**
 * Calculate age from date of birth
 */
export function calculateAge(dateOfBirth: string): number {
  const dob = new Date(dateOfBirth)
  const today = new Date()
  let age = today.getFullYear() - dob.getFullYear()
  const monthDiff = today.getMonth() - dob.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--
  }

  return age
}
